import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { User, Edit, Plus, Vote, FileText, ListTodo, Trophy, ChevronRight, Loader2, CheckCircle, Clock, TrendingUp, TrendingDown, Trash2, AlertTriangle, RotateCcw, Mail, Phone, UserCircle, Shield, ChevronDown, Key, ExternalLink } from 'lucide-react';

function ProfilePage() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts'); // posts, votes, tasks, leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [userRank, setUserRank] = useState(null);
  const [voteStats, setVoteStats] = useState({ received: 0, unused: 0 });
  const [voteStatements, setVoteStatements] = useState([]);
  const [adminVotes, setAdminVotes] = useState([]);
  const [earnMethods, setEarnMethods] = useState([]);
  const [userTasks, setUserTasks] = useState({ completed: [], outstanding: [] });
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Vote statements pagination
  const [statementsLimit, setStatementsLimit] = useState(20);
  const [hasMoreStatements, setHasMoreStatements] = useState(false);
  
  // Voting on profile
  const [isVoting, setIsVoting] = useState(false);
  const [showAdminVotePanel, setShowAdminVotePanel] = useState(false);
  const [adminVoteAction, setAdminVoteAction] = useState('increase');
  const [adminVoteCount, setAdminVoteCount] = useState(10);
  const [adminVoteReason, setAdminVoteReason] = useState('');
  
  // Admin password reset
  const [resetPassword, setResetPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    checkCurrentUser();
    fetchUserData();
    fetchUserPosts();
  }, [userId]);

  useEffect(() => {
    if (user && currentUser) {
      fetchVoteStats();
      fetchUserRank();
      // Check deletion status if viewing own profile
      if (currentUser.user_id === user.user_id) {
        fetchDeletionStatus();
      }
    }
  }, [user, currentUser]);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    } else if (activeTab === 'votes' && (isOwnProfile || isAdmin)) {
      fetchVoteStatements();
      fetchAdminVotes();
      fetchEarnMethods();
    } else if (activeTab === 'tasks') {
      fetchUserTasks();
      fetchEarnMethods();
    }
  }, [activeTab]);

  const checkCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setCurrentUser(data);
      }
    } catch (error) {
      // User not logged in
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${API}/users/${userId}`);
      const data = await response.json();
      if (!response.ok) throw new Error('User not found');
      setUser(data);
    } catch (error) {
      toast.error('Failed to load user profile');
      navigate('/feed');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await fetch(`${API}/posts?user_id=${userId}`);
      const data = await response.json();
      if (response.ok) setPosts(data);
    } catch (error) {
      console.error('Failed to load posts');
    }
  };

  const fetchVoteStats = async () => {
    try {
      // Get votes received (votes cast FOR this user as an artist)
      const votesResponse = await fetch(`${API}/votes/artist/${userId}/count`);
      const votesData = await votesResponse.json();
      
      // Unused votes = free_votes_remaining from user data
      setVoteStats({
        received: votesData?.total_votes || 0,
        unused: user?.free_votes_remaining || 0
      });
    } catch (error) {
      console.error('Failed to fetch vote stats');
    }
  };

  const fetchUserRank = async () => {
    try {
      const response = await fetch(`${API}/votes/leaderboard`);
      const data = await response.json();
      if (response.ok) {
        // Filter out demo users and find rank
        const filtered = data.filter(a => !a.artist_id?.startsWith('demo_'));
        const rank = filtered.findIndex(a => a.artist_id === userId) + 1;
        setUserRank(rank > 0 ? rank : null);
      }
    } catch (error) {
      console.error('Failed to fetch rank');
    }
  };

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const response = await fetch(`${API}/votes/leaderboard`);
      const data = await response.json();
      if (response.ok) {
        // Filter out demo users
        const filtered = data.filter(a => !a.artist_id?.startsWith('demo_'));
        setLeaderboard(filtered);
      }
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const fetchVoteStatements = async () => {
    try {
      const response = await fetch(`${API}/votes/statements/${userId}`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setVoteStatements(data);
        setHasMoreStatements(data.length > statementsLimit);
      }
    } catch (error) {
      console.error('Failed to fetch vote statements');
    }
  };

  const fetchAdminVotes = async () => {
    try {
      const response = await fetch(`${API}/admin/votes/${userId}`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) setAdminVotes(data);
    } catch (error) {
      console.error('Failed to fetch admin votes');
    }
  };

  // Handle voting for artist on their profile
  const handleVoteForArtist = async () => {
    if (!currentUser) {
      toast.error('Please login to vote');
      navigate('/login');
      return;
    }
    
    if (!user?.is_artist) {
      toast.error('Can only vote for artists');
      return;
    }
    
    setIsVoting(true);
    try {
      const response = await fetch(`${API}/votes?artist_id=${userId}&event_id=default_event&vote_count=1`, {
        method: 'POST',
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Vote cast successfully!');
        fetchVoteStats();
        fetchVoteStatements();
      } else {
        if (data.detail?.includes('Not enough free votes') || data.detail?.includes('No votes remaining')) {
          toast.error('No votes remaining. Buy more votes to continue!');
        } else {
          toast.error(data.detail || 'Failed to vote');
        }
      }
    } catch (error) {
      toast.error('Failed to vote');
    } finally {
      setIsVoting(false);
    }
  };

  // Handle admin vote adjustment
  const handleAdminVoteSubmit = async () => {
    if (!adminVoteReason.trim()) {
      toast.error('Please provide a reason for the vote adjustment');
      return;
    }
    
    setIsVoting(true);
    try {
      const response = await fetch(`${API}/admin/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          artist_id: userId, 
          vote_count: adminVoteCount, 
          action: adminVoteAction, 
          reason: adminVoteReason 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Votes ${adminVoteAction}d successfully!`);
        setShowAdminVotePanel(false);
        setAdminVoteReason('');
        fetchVoteStats();
        fetchAdminVotes();
      } else {
        toast.error(data.detail || 'Failed to adjust votes');
      }
    } catch (error) {
      toast.error('Failed to adjust votes');
    } finally {
      setIsVoting(false);
    }
  };

  const loadMoreStatements = () => {
    setStatementsLimit(prev => prev + 20);
  };

  // Admin: Reset user password
  const handleAdminResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setIsResettingPassword(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_password: resetPassword })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Password reset successfully');
        setResetPassword('');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch { toast.error('Failed to reset password'); }
    finally { setIsResettingPassword(false); }
  };

  // Admin: Permanently delete user
  const handleAdminDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${user?.name}? This cannot be undone.`)) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/permanent-delete`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('User permanently deleted');
        navigate('/admin');
      } else {
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch { toast.error('Failed to delete user'); }
    finally { setDeleteLoading(false); }
  };

  const fetchEarnMethods = async () => {
    try {
      const response = await fetch(`${API}/earn-methods`);
      const data = await response.json();
      if (response.ok) setEarnMethods(data);
    } catch (error) {
      console.error('Failed to fetch earn methods');
    }
  };

  const fetchUserTasks = async () => {
    try {
      const response = await fetch(`${API}/user/tasks/${userId}`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) setUserTasks(data);
    } catch (error) {
      console.error('Failed to fetch user tasks');
    }
  };

  const fetchDeletionStatus = async () => {
    try {
      const response = await fetch(`${API}/users/deletion-status`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) setDeletionStatus(data);
    } catch (error) {
      console.error('Failed to fetch deletion status');
    }
  };

  const handleRequestDeletion = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/users/request-deletion`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Deletion request submitted');
        setShowDeleteModal(false);
        fetchDeletionStatus();
      } else {
        toast.error(data.detail || 'Failed to submit deletion request');
      }
    } catch (error) {
      toast.error('Failed to submit deletion request');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/users/cancel-deletion`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Deletion request cancelled');
        fetchDeletionStatus();
      } else {
        toast.error(data.detail || 'Failed to cancel deletion request');
      }
    } catch (error) {
      toast.error('Failed to cancel deletion request');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRecoverAccount = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/recover-account`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Account recovered successfully');
        fetchUserData();
      } else {
        toast.error(data.detail || 'Failed to recover account');
      }
    } catch (error) {
      toast.error('Failed to recover account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const isOwnProfile = currentUser && user && currentUser.user_id === user.user_id;
  const isAdmin = currentUser?.is_admin;
  const canViewVotesTab = isOwnProfile || isAdmin;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">User not found</div>;
  }

  // Talent colors
  const talentColors = [
    'bg-yellow-500 text-black',
    'bg-gray-600 text-white',
    'bg-orange-300 text-black',
    'bg-purple-500 text-white',
    'bg-green-500 text-white',
    'bg-pink-500 text-white',
  ];

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6">
        
        {/* Profile Header Section */}
        <div className="text-center mb-8">
          {/* Artist Name */}
          <h1 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 bg-clip-text text-transparent">
            {user.stage_name || user.name}
          </h1>
          
          {/* Profile Picture with Floating Talent Buttons */}
          <div className="relative inline-block mb-6">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden bg-black border-4 border-primary/30 mx-auto">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-16 w-16 text-gray-600" />
                </div>
              )}
            </div>
            
            {/* Floating Talent Buttons */}
            {user.talents && user.talents.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {user.talents.map((talent, idx) => (
                  <span
                    key={idx}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium ${talentColors[idx % talentColors.length]}`}
                  >
                    {talent}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Edit Profile Button (only for own profile) */}
          {isOwnProfile && (
            <div className="flex flex-col items-center gap-3 mb-6">
              <div className="flex justify-center gap-3">
                <Button
                  onClick={() => navigate('/edit-profile')}
                  className="bg-primary rounded-full"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
                <Button
                  onClick={() => navigate('/create-post')}
                  className="bg-secondary text-black rounded-full"
                  data-testid="create-post-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Post
                </Button>
              </div>
              
              {/* Deletion Status Banner */}
              {deletionStatus?.has_pending_request && deletionStatus.status === 'pending' && (
                <div className="w-full max-w-md p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-xl text-center">
                  <p className="text-yellow-400 text-sm mb-2">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    Account deletion pending admin approval
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelDeletion}
                    disabled={deleteLoading}
                    className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Cancel Request
                  </Button>
                </div>
              )}

              {/* Delete Account Button - only show if no pending deletion and NOT an admin */}
              {!deletionStatus?.has_pending_request && !currentUser?.is_admin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Account
                </Button>
              )}
            </div>
          )}

          {/* Admin: Show if user is deleted */}
          {isAdmin && user?.deletion_status === 'deleted' && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-center">
              <p className="text-red-400 mb-3">
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                This account has been deleted
              </p>
              <Button
                onClick={handleRecoverAccount}
                disabled={deleteLoading}
                className="bg-green-500 hover:bg-green-600"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Recover Account
              </Button>
            </div>
          )}

          {/* Delete Account Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="glass-panel w-full max-w-md p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Delete Account?</h2>
                  <p className="text-muted-foreground text-sm">
                    Your account will be submitted for deletion. An admin will review your request.
                    You can recover your account within 30 days if you change your mind.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 rounded-full"
                    disabled={deleteLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRequestDeletion}
                    disabled={deleteLoading}
                    className="flex-1 bg-red-500 hover:bg-red-600 rounded-full"
                    data-testid="confirm-delete-btn"
                  >
                    {deleteLoading ? 'Submitting...' : 'Delete Account'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Artist Registration Note */}
        {(isOwnProfile || isAdmin) && (
          <div className="mb-6">
            {user?.is_artist ? (
              <p className="text-green-500 font-bold text-sm" data-testid="artist-status-registered">
                Note - You are registered as an artist
              </p>
            ) : (
              <div>
                <Button
                  onClick={() => navigate('/Sign-up')}
                  className="bg-purple-600 hover:bg-purple-700 rounded-full mb-2"
                  data-testid="register-as-artist-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Register as Artist/Group
                </Button>
                <p className="text-red-500 font-bold text-sm" data-testid="artist-status-not-registered">
                  Note - You have not registered for the talent show
                </p>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
          <Card className="bg-blue-900 p-4 text-center rounded-xl">
            <p className="text-xs md:text-sm text-blue-200 mb-1">Your Votes</p>
            <p className="text-2xl md:text-4xl font-bold text-white" data-testid="votes-received">
              {voteStats.received}
            </p>
          </Card>
          <Card className="bg-blue-900 p-4 text-center rounded-xl">
            <p className="text-xs md:text-sm text-blue-200 mb-1">Unused Votes</p>
            <p className="text-2xl md:text-4xl font-bold text-white" data-testid="votes-unused">
              {voteStats.unused}
            </p>
          </Card>
          <Card className="bg-blue-900 p-4 text-center rounded-xl">
            <p className="text-xs md:text-sm text-blue-200 mb-1">Leadership</p>
            <p className="text-2xl md:text-4xl font-bold text-white" data-testid="user-rank">
              {userRank || '-'}
            </p>
          </Card>
        </div>

        {/* Tab Buttons */}
        <div className={`grid gap-2 mb-6 ${isAdmin && !isOwnProfile ? 'grid-cols-5' : 'grid-cols-4'}`}>
          {canViewVotesTab && (
            <Button
              onClick={() => setActiveTab('votes')}
              className={`rounded-lg text-xs md:text-sm py-3 ${activeTab === 'votes' ? 'bg-orange-500 text-white' : 'bg-blue-900 hover:bg-blue-800'}`}
              data-testid="tab-votes"
            >
              <Vote className="h-4 w-4 mr-1 md:mr-2 hidden md:inline" />
              Votes
            </Button>
          )}
          <Button
            onClick={() => setActiveTab('posts')}
            className={`rounded-lg text-xs md:text-sm py-3 ${activeTab === 'posts' ? 'bg-orange-500 text-white' : 'bg-blue-900 hover:bg-blue-800'}`}
            data-testid="tab-posts"
          >
            <FileText className="h-4 w-4 mr-1 md:mr-2 hidden md:inline" />
            Posts
          </Button>
          <Button
            onClick={() => setActiveTab('tasks')}
            className={`rounded-lg text-xs md:text-sm py-3 ${activeTab === 'tasks' ? 'bg-orange-500 text-white' : 'bg-blue-900 hover:bg-blue-800'}`}
            data-testid="tab-tasks"
          >
            <ListTodo className="h-4 w-4 mr-1 md:mr-2 hidden md:inline" />
            Tasks
          </Button>
          <Button
            onClick={() => setActiveTab('leaderboard')}
            className={`rounded-lg text-xs md:text-sm py-3 ${activeTab === 'leaderboard' ? 'bg-orange-500 text-white' : 'bg-blue-900 hover:bg-blue-800'}`}
            data-testid="tab-leaderboard"
          >
            <Trophy className="h-4 w-4 mr-1 md:mr-2 hidden md:inline" />
            Board
          </Button>
          {isAdmin && !isOwnProfile && (
            <Button
              onClick={() => setActiveTab('admin')}
              className={`rounded-lg text-xs md:text-sm py-3 ${activeTab === 'admin' ? 'bg-orange-500 text-white' : 'bg-purple-900 hover:bg-purple-800'}`}
              data-testid="tab-admin"
            >
              <Shield className="h-4 w-4 mr-1 md:mr-2 hidden md:inline" />
              Admin
            </Button>
          )}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          
          {/* VOTES TAB */}
          {activeTab === 'votes' && canViewVotesTab && (
            <div className="space-y-6">

              {/* Vote for Artist Section - Only show for artists when viewing someone else's profile */}
              {user?.is_artist && !isOwnProfile && (
                <Card className="glass-panel p-6 rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 border-primary/30">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-bold mb-1 text-white">Vote for {user?.stage_name || user?.name}</h2>
                      <p className="text-gray-300 text-sm">
                        {currentUser?.is_admin 
                          ? 'As admin, you have unlimited votes' 
                          : `You have ${currentUser?.free_votes_remaining || 0} votes remaining`}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleVoteForArtist}
                        disabled={isVoting}
                        className="bg-primary hover:bg-primary/80 text-white rounded-full px-6 py-2 font-bold"
                        data-testid="profile-vote-btn"
                      >
                        {isVoting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Vote className="h-4 w-4 mr-2" />
                        )}
                        Vote +1
                      </Button>
                      
                      {isAdmin && (
                        <Button
                          onClick={() => setShowAdminVotePanel(!showAdminVotePanel)}
                          className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-4 py-2"
                          data-testid="admin-vote-toggle-btn"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Admin Vote
                          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showAdminVotePanel ? 'rotate-180' : ''}`} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Admin Vote Panel */}
                  {isAdmin && showAdminVotePanel && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="space-y-3 bg-purple-900/50 p-4 rounded-xl">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => setAdminVoteAction('increase')}
                            className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-gray-600'}`}
                          >
                            <TrendingUp className="h-4 w-4 mr-1" />
                            Increase
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setAdminVoteAction('decrease')}
                            className={`flex-1 rounded-full ${adminVoteAction === 'decrease' ? 'bg-red-600' : 'bg-gray-600'}`}
                          >
                            <TrendingDown className="h-4 w-4 mr-1" />
                            Decrease
                          </Button>
                        </div>

                        <Input
                          type="number"
                          value={adminVoteCount}
                          onChange={(e) => setAdminVoteCount(parseInt(e.target.value) || 0)}
                          placeholder="Vote count"
                          min={1}
                          className="bg-white/10 border-white/20"
                        />

                        <Textarea
                          value={adminVoteReason}
                          onChange={(e) => setAdminVoteReason(e.target.value)}
                          placeholder="Reason for vote adjustment (required)"
                          className="bg-white/10 border-white/20"
                          rows={2}
                        />

                        <Button
                          onClick={handleAdminVoteSubmit}
                          disabled={isVoting || !adminVoteReason.trim()}
                          className={`w-full rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                        >
                          {isVoting ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          Submit {adminVoteAction === 'increase' ? '+' : '-'}{adminVoteCount} Votes
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* Get More Votes Section - Only show on own profile */}
              {isOwnProfile && (
                <Card className="glass-panel p-6 rounded-xl bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-orange-500/30">
                  <h2 className="text-xl font-bold mb-2 text-white">Get More Votes</h2>
                  <p className="text-gray-300 mb-4">Complete your tasks and get more votes</p>
                  <Button
                    onClick={() => navigate('/earn-votes')}
                    className="bg-orange-500 hover:bg-orange-400 text-white rounded-full px-6 py-2 font-bold"
                    data-testid="go-to-tasks-btn"
                  >
                    Go To Tasks
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </Card>
              )}

              {/* Votes Statements Section */}
              <div>
                <h2 className="text-xl font-bold mb-4 text-white">VOTES STATEMENTS</h2>
                <Card className="overflow-hidden rounded-xl">
                  <div className="max-h-[500px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-blue-900 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-white font-medium">Date</th>
                          <th className="px-4 py-3 text-center text-white font-medium">Votes</th>
                          <th className="px-4 py-3 text-left text-white font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Admin Votes (show first) */}
                        {adminVotes.map((adminVote, idx) => (
                          <tr key={`admin-${idx}`} className={`border-b border-gray-300 ${adminVote.vote_count > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                            <td className="px-4 py-3 text-gray-800 text-sm">
                              {new Date(adminVote.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 text-center font-bold flex items-center justify-center gap-1">
                              {adminVote.vote_count > 0 ? (
                                <>
                                  <TrendingUp className="h-4 w-4 text-green-600" />
                                  <span className="text-green-700">+{adminVote.vote_count}</span>
                                </>
                              ) : (
                                <>
                                  <TrendingDown className="h-4 w-4 text-red-600" />
                                  <span className="text-red-700">{adminVote.vote_count}</span>
                                </>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-800 text-sm">
                              <span className="inline-block px-2 py-0.5 bg-purple-200 text-purple-800 text-xs rounded mr-2">Admin</span>
                              {adminVote.reason}
                            </td>
                          </tr>
                        ))}
                        {/* All Vote Statements - larger votes individual, +1 votes grouped by date+reason */}
                        {voteStatements.length === 0 && adminVotes.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground bg-gray-800">
                              No vote history yet
                            </td>
                          </tr>
                        ) : (
                          (() => {
                            const bigVotes = voteStatements.filter(s => s.votes !== 1);
                            const smallVotes = voteStatements.filter(s => s.votes === 1);
                            
                            // Group +1 votes by date + reason
                            const grouped = smallVotes.reduce((acc, s) => {
                              const dateKey = new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              const key = `${dateKey}-${s.reason}`;
                              if (!acc[key]) acc[key] = { date: s.date, reason: s.reason, votes: 0, count: 0 };
                              acc[key].votes += 1;
                              acc[key].count += 1;
                              return acc;
                            }, {});
                            
                            // Combine and sort: largest votes first
                            const allRows = [
                              ...bigVotes.map(s => ({ ...s, count: 1 })),
                              ...Object.values(grouped)
                            ].sort((a, b) => b.votes - a.votes);
                            
                            return allRows.map((row, idx) => (
                              <tr key={idx} className="bg-gray-200 border-b border-gray-300">
                                <td className="px-4 py-3 text-gray-800 text-sm">
                                  {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </td>
                                <td className={`px-4 py-3 text-center font-bold ${row.votes >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  {row.votes > 0 ? '+' : ''}{row.votes}
                                  {row.count > 1 && <span className="text-xs text-gray-500 ml-1">({row.count}x)</span>}
                                </td>
                                <td className="px-4 py-3 text-gray-800 text-sm">{row.reason}</td>
                              </tr>
                            ));
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* MY POSTS TAB */}
          {activeTab === 'posts' && (
            <div>
              <h2 className="text-xl font-bold mb-4 text-white">MY POSTS</h2>
              {posts.length === 0 ? (
                <Card className="glass-panel p-12 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">No posts yet</p>
                  {isOwnProfile && (
                    <Button
                      onClick={() => navigate('/create-post')}
                      className="bg-primary rounded-full"
                    >
                      Create Your First Post
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {posts.map((post) => (
                    <Card
                      key={post.post_id}
                      className="glass-panel overflow-hidden group cursor-pointer hover:border-primary/50 transition-colors"
                      data-testid={`profile-post-${post.post_id}`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={post.media_url}
                          alt="Post"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/300?text=Media'; }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center">
                            <p className="font-bold">{post.likes_count || 0} likes</p>
                            <p className="text-sm">{post.comments_count || 0} comments</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MY TASKS TAB */}
          {activeTab === 'tasks' && (
            <div className="space-y-6">
              {/* Outstanding Tasks */}
              <div>
                <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-500" />
                  Outstanding Tasks
                </h2>
                <Card className="overflow-hidden rounded-xl">
                  <table className="w-full">
                    <thead className="bg-blue-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-medium">Task</th>
                        <th className="px-4 py-3 text-center text-white font-medium">Reward</th>
                        <th className="px-4 py-3 text-right text-white font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!userTasks.outstanding || userTasks.outstanding.length === 0) ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground bg-gray-800">
                            All tasks completed! 🎉
                          </td>
                        </tr>
                      ) : (
                        userTasks.outstanding.map((task, idx) => (
                          <tr key={task.task_id || idx} className="bg-gray-200 border-b border-gray-300">
                            <td className="px-4 py-3">
                              <span className="text-gray-800 font-medium">{task.task_name}</span>
                              {task.rules && (
                                <p className="text-xs text-gray-500 mt-1">{task.rules}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-blue-600 font-bold">+{task.points} votes</td>
                            <td className="px-4 py-3 text-right">
                              {task.action_button_link ? (
                                <Button
                                  size="sm"
                                  className="bg-primary rounded-full text-xs"
                                  onClick={() => {
                                    if (task.action_button_link.startsWith('http')) {
                                      window.open(task.action_button_link, '_blank');
                                    } else {
                                      navigate(task.action_button_link);
                                    }
                                  }}
                                >
                                  {task.action_button_text || 'Do Task'}
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-500">Manual</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Card>
              </div>

              {/* Completed Tasks */}
              <div>
                <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Completed Tasks
                </h2>
                <Card className="overflow-hidden rounded-xl">
                  <table className="w-full">
                    <thead className="bg-blue-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-medium">Task</th>
                        <th className="px-4 py-3 text-center text-white font-medium">Points Earned</th>
                        <th className="px-4 py-3 text-right text-white font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!userTasks.completed || userTasks.completed.length === 0) ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground bg-gray-800">
                            Complete tasks above to earn votes!
                          </td>
                        </tr>
                      ) : (
                        userTasks.completed.map((task, idx) => (
                          <tr key={task.task_id || idx} className="bg-gray-200 border-b border-gray-300">
                            <td className="px-4 py-3 text-gray-800">{task.task_name}</td>
                            <td className="px-4 py-3 text-center text-green-600 font-bold">+{task.points}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                <CheckCircle className="h-4 w-4" />
                                Done
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Card>
              </div>
            </div>
          )}

          {/* LEADERBOARD TAB */}
          {activeTab === 'leaderboard' && (
            <div>
              <h2 className="text-xl font-bold mb-4 text-white">LEADERSHIP BOARD</h2>
              {leaderboardLoading ? (
                <Card className="glass-panel p-12 text-center">
                  <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
                  <p className="text-lg text-muted-foreground">Loading......</p>
                </Card>
              ) : leaderboard.length === 0 ? (
                <Card className="glass-panel p-12 text-center">
                  <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No artists on the leaderboard yet</p>
                </Card>
              ) : (
                <Card className="overflow-hidden rounded-xl">
                  <table className="w-full">
                    <thead className="bg-blue-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-medium">Rank</th>
                        <th className="px-4 py-3 text-left text-white font-medium">Artist</th>
                        <th className="px-4 py-3 text-right text-white font-medium">Votes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((artist, idx) => (
                        <tr
                          key={artist.artist_id}
                          className={`border-b border-gray-300 cursor-pointer hover:bg-gray-300 transition-colors ${
                            artist.artist_id === userId ? 'bg-yellow-200' : 'bg-gray-200'
                          }`}
                          onClick={() => navigate(`/profile/${artist.artist_id}`)}
                        >
                          <td className="px-4 py-3 text-gray-800 font-bold">
                            {idx + 1 <= 3 ? (
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                                idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-orange-400'
                              } text-white font-bold`}>
                                {idx + 1}
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-8 h-8">
                                {idx + 1}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-800">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-400">
                                {artist.artist_picture ? (
                                  <img src={artist.artist_picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <User className="h-5 w-5 text-gray-600" />
                                  </div>
                                )}
                              </div>
                              <span className="font-medium">{artist.artist_name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800 font-bold">{artist.total_votes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </div>
          )}

          {/* ADMIN TAB */}
          {activeTab === 'admin' && isAdmin && !isOwnProfile && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white">ADMIN CONTROLS</h2>
              
              {/* User Information */}
              <Card className="glass-panel p-6 rounded-xl bg-purple-900/30 border-purple-500/30">
                <h3 className="text-sm font-bold text-purple-300 mb-4 flex items-center gap-2">
                  <UserCircle className="h-4 w-4" />
                  User Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white font-medium">{user?.name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Email:</span>
                    <span className="text-white font-medium">{user?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Phone:</span>
                    <span className="text-white font-medium">{user?.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Role:</span>
                    <span className="text-white font-medium">
                      {user?.is_admin ? 'Admin' : user?.is_judge ? 'Judge' : user?.is_artist ? 'Artist' : 'Supporter'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Joined:</span>
                    <span className="text-white font-medium">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Vote className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Artist:</span>
                    <span className={`font-medium ${user?.is_artist ? 'text-green-400' : 'text-red-400'}`}>
                      {user?.is_artist ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Reset Password */}
              <Card className="glass-panel p-6 rounded-xl" data-testid="admin-reset-password-card">
                <h3 className="text-sm font-bold text-yellow-300 mb-4 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Reset User Password
                </h3>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Enter new password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="bg-white/5 border-white/10 focus:border-yellow-500 rounded-xl"
                    data-testid="admin-new-password-input"
                  />
                  <Button
                    onClick={handleAdminResetPassword}
                    disabled={isResettingPassword || !resetPassword}
                    className="bg-yellow-600 hover:bg-yellow-700 rounded-full whitespace-nowrap"
                    data-testid="admin-reset-password-btn"
                  >
                    {isResettingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
                  </Button>
                </div>
              </Card>

              {/* Delete User */}
              <Card className="glass-panel p-6 rounded-xl border-red-500/30" data-testid="admin-delete-user-card">
                <h3 className="text-sm font-bold text-red-400 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Danger Zone
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Permanently delete this user and all their data. This action cannot be undone.
                </p>
                <Button
                  onClick={handleAdminDeleteUser}
                  disabled={deleteLoading}
                  className="bg-red-600 hover:bg-red-700 rounded-full"
                  data-testid="admin-delete-user-btn"
                >
                  {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Permanently Delete User
                </Button>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
